import {
  ARCHIVE_ENTITY_TYPE_COMPLIANCE,
  CREATE_COMPLIANCE_RULE,
  GET_ASSIGNED_RULES,
  GET_COMPLIANCE_RULE_HISTORY,
  RESTORE_COMPLIANCE_RULE_VERSION,
  UNARCHIVE_ENTITY_TYPE_COMPLIANCE,
  UPDATE_COMPLIANCE_RULE,
  VIEW_ADMIN_LIST,
  VIEW_ENTITY_TYPE_URL,
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import formatting_mixins from '@/utils/mixins/formatting_mixins';
import axios from "axios";

import 'datatables.net';
import 'datatables.net-dt/css/dataTables.dataTables.css';

export default {
  name: 'ComplianceRules',
  mixins: [alert_mixin , formatting_mixins],

  methods:{

        async create_a_compliance_rule() {
      if (!this.validate_rule()) return;

      this.loading = true;
      try {

        const payload = {
          name: this.new_rule.name,
          description: this.new_rule.description,
          data_point_id: this.new_rule.data_point_id,
          frequency: this.new_rule.frequency,
          grace_period_days: this.computed_grace_period_days,
          is_active: this.new_rule.is_active,
          project_id: this.new_rule.project_id,
          entity_type_id: this.entity_type_id, // Add entity_type_id to payload
          validation_logic: {
            allowed_age: this.computed_allowed_age
          },
          enforcement_actions: {},
          notified_user_ids: this.new_rule.notify_admin_ids,

        };

        await axios.post(CREATE_COMPLIANCE_RULE, payload, {
          headers: this.headers
        });

        this.show_success("Compliance rule created successfully");
        window.location.reload(); // Reload to reflect changes in the table
        this.reset_rule_form();
        await this.fetch_compliance_rules();
      } catch (error) {
        this.show_error("Error creating compliance rule: " + (error.response?.data?.message || error.message));
      } finally {
        this.loading = false;
      }
    },

    async update_compliance_rule() {
      if (!this.validate_edit_rule()) return;

      this.loading = true;
      try {
        const payload = {
          rule_id: this.edit_rule.id,
          name: this.edit_rule.name,
          description: this.edit_rule.description,
          data_point_id: this.edit_rule.data_point_id,
          frequency: this.edit_rule.frequency,
          grace_period_days: this.computed_edit_grace_period_days,
          validation_logic: {
            allowed_age: this.computed_edit_allowed_age
          },
          enforcement_actions: JSON.parse(this.edit_rule.enforcement_actions),
          is_active: this.edit_rule.is_active,
          project_id: this.project_id
        };

        await axios.post(UPDATE_COMPLIANCE_RULE, payload, {
          headers: this.headers
        });

        this.show_success("Compliance rule updated successfully");
        await this.fetch_compliance_rules();
      } catch (error) {
        this.show_error("Error updating compliance rule" || error.message );
      } finally {
        this.loading = false;
      }
    },

    fetch_compliance_rules() {
      this.table_loading = true;
      this.raw_rules_data = [];
      this.table_data = [];

      const request_data = {
        project_id: this.project_id,
        entity_type_id: this.entity_type_id,
        include_inactive_rules: true,
        include_inactive_assignments: true,
        include_archived_rules: this.show_archived
      };

      axios.post(GET_ASSIGNED_RULES, request_data, { headers: this.headers })
        .then(response => {
          const response_data = response.data.data ?? response.data ?? {};
          const rules_array = response_data.assigned_rules ?? [];

          const processed_data = rules_array.map(this.normalize_rule);

          const presented_data = processed_data.map(this.transform_rule_to_presented_data);

          const table_headers = presented_data.length > 0
            ? Object.keys(presented_data[0])
            : [];

          this.table_safe_headers = table_headers;
          this.raw_rules_data = processed_data;
          this.table_data = presented_data;

          this.$nextTick(() => {
            this.init_data_table();
          });
        })
        .catch(error => {
          this.show_error(
            "Error loading compliance rules: " +
            (error.response?.data?.message ?? error.message)
          );
          this.table_data = [];
        })
        .finally(() => {
          this.table_loading = false;
        });
    },

    normalize_rule(rule, index) {
      // Helper function for nullish coalescing
      const value = (val, def) => val ?? def;

      // Precompute complex properties separately
      const DATA_POINT_NAME = this.get_data_point_name(rule?.data_point) ?? 'N/A';
      const FORMATTED_FREQUENCY = this.format_frequency(rule?.frequency) ?? 'N/A';

      var normalized_rule = {
        id: value(rule?.id, index),
        name: value(rule?.name, 'N/A'),
        description: value(rule?.description, 'N/A'),
        data_point: DATA_POINT_NAME,
        frequency: FORMATTED_FREQUENCY,
        status: rule?.is_active ? 'Active' : 'Inactive',
        archived: value(rule?.assignment?.is_archived, false),
        is_required: value(rule?.assignment?.is_required, false),
        assigned_at: value(rule?.assignment?.assigned_at, 'N/A'),
        _rawData: rule
      }

      return normalized_rule;
    },

    transform_rule_to_presented_data(rule, index) {
      var rule_details = {
        id: rule.id ?? index,
        name: rule.name ?? 'N/A',
        description: rule.description ?? 'N/A',
        data_point: rule.data_point,
        frequency: rule.frequency,
        status: rule.status,
        archived: rule.archived ? 'Yes' : 'No',
        assigned_at: this.format_date(rule.assigned_at),
      };
      return rule_details
    },

    async view_rule_history(rule) {
      this.history_loading = true;

      try {
        this.selected_rule_name = rule.name || 'Unknown Rule';

        const request_data = {
          rule_id: rule.id
        };

        const response = await axios.post(
          GET_COMPLIANCE_RULE_HISTORY,
          request_data,
          { headers: this.headers }
        );

        // Process the response data for timeline display
        const history_data = response.data.data || [];
        this.rule_history_data = history_data
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .map(entry => ({
            ...entry,
            // Add any additional processing if needed
          }));

      } catch (error) {
        this.show_error("Error loading rule history: " + (error.response?.data?.message || error.message));
      } finally {
        this.history_loading = false;
      }
    },

    async archive_rule(rule, reason = null) {
      if (!reason) {
        this.show_archive_modal(rule, 'archive');
        return;
      }

      this.archiving_rules.add(rule.id);

      try {
        await axios.post(
          ARCHIVE_ENTITY_TYPE_COMPLIANCE,
          {
            entity_type_id: parseInt(this.entity_type_id, 10),
            rule_id: rule.id,
            archive_reason: reason
          },
          { headers: this.headers }
        );

        this.show_success("Rule archived successfully");
        await this.fetch_compliance_rules();

      } catch (error) {
        this.show_error("Error archiving rule: " + (error.response?.data?.message || error.message));
      } finally {
        this.archiving_rules.delete(rule.id);
      }
    },

        async unarchive_rule(rule, reason = null) {
      if (!reason) {
        this.show_archive_modal(rule, 'unarchive');
        return;
      }

      this.unarchiving_rules.add(rule.id);

      try {
        await axios.post(
          UNARCHIVE_ENTITY_TYPE_COMPLIANCE,
          {
            entity_type_id: parseInt(this.entity_type_id, 10),
            rule_id: rule.id,
            unarchive_reason: reason
          },
          { headers: this.headers }
        );

        this.show_success("Rule unarchived successfully");
        await this.fetch_compliance_rules();

      } catch (error) {
        this.show_error("Error unarchiving rule: " + (error.response?.data?.message || error.message));
      } finally {
        this.unarchiving_rules.delete(rule.id);
      }
    },

    async restore_rule_version(version_id) {
      if (!confirm("Are you sure you want to restore this rule version as the current active version?")) {
        return;
      }

      await axios.post(
        RESTORE_COMPLIANCE_RULE_VERSION,
        { rule_id: version_id },
        { headers: this.headers }
      )
      .then(async () => {
        this.show_success("Rule version restored successfully");

        const current_rule = this.rule_history_data.find(r => r.id === version_id);
        if (current_rule) {
          await this.view_rule_history(current_rule);
        }
        await this.fetch_compliance_rules();
      })
      .catch((error) => {
        this.show_error("Error restoring rule version: " + (error.response?.data?.message || error.message));
      });
    },

    // Archive/Unarchive methods
    is_rule_archiving(rule_id) {
      return this.archiving_rules.has(rule_id);
    },

    is_rule_unarchiving(rule_id) {
      return this.unarchiving_rules.has(rule_id);
    },

    async fetch_entity_types() {
      this.entity_types_loading = true;
      try {
        const response = await axios.get(VIEW_ENTITY_TYPE_URL, {
          params: { project_id: this.project_id },
          headers: this.headers
        });

        const data = response.data.data || response.data || [];
        this.entity_types = Array.isArray(data) ? data : [];

      } catch (error) {
        this.show_error("Error loading entity types: " + (error.response?.data?.message || error.message));
        this.entity_types = [];
      } finally {
        this.entity_types_loading = false;
      }
    },

    edit_the_rule(rule) {
      const allowed_age = rule.validation_logic?.allowed_age || null;
      const grace_period_days = rule.grace_period_days || 0;

      // Determine if file age was a preset value or custom
      const preset_age_values = this.get_preset_values('file_age');
      let age_selection = '';
      let custom_age_days = null;

      if (allowed_age) {
        if (preset_age_values.includes(allowed_age)) {
          age_selection = allowed_age.toString();
        } else {
          age_selection = 'custom';
          custom_age_days = allowed_age;
        }
      }

      // Determine if grace period was a preset value or custom
      const preset_grace_values = this.get_preset_values('grace_period');
      let grace_period_selection = '';
      let custom_grace_period_days = null;

      if (preset_grace_values.includes(grace_period_days)) {
        grace_period_selection = grace_period_days.toString();
      } else {
        grace_period_selection = 'custom';
        custom_grace_period_days = grace_period_days;
      }

      this.edit_rule = {
        ...rule,
        data_point_id: rule.data_point,
        age_selection: age_selection,
        custom_age_days: custom_age_days,
        grace_period_selection: grace_period_selection,
        custom_grace_period_days: custom_grace_period_days,
        enforcement_actions: JSON.stringify(rule.enforcement_actions, null, 2)
      };
    },

    fetch_admin_options() {
      axios.get(VIEW_ADMIN_LIST, { headers: this.headers })
      .then(response => {
        this.admin_options = response.data.data || [];
      })
      .catch (error => {
        this.show_error(error)
        this.admin_options = [];
      });
    },

  }
}